import posthog from "posthog-js"
import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import routeMessageTemplates from "@/lib/routeMessageTemplates.json"
import { getTextSummary, LangTemplate } from "../maps/mapUtils"
import { OrderAnalyzeRoute } from "@/lib/orderAnalyzeRouteParser"
import { shouldShowOrderMessageExtraNotes } from "@/lib/orderMessageHostRules"

// Escapes a string so it can be used as a literal inside a RegExp.
const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

// oskargroup sends the message to drivers by SMS and does not want the order
// number in it. Read here rather than at every caller: half of them build the
// message without passing any of the display options.
export const HIDE_ORDER_NUMBER_FLAG = "hide_order_number_in_route_messages"

const formatKm = (distanceMeters: number): string =>
  `${(distanceMeters / 1000).toFixed(0)} km`

/**
 * Stops of the order being sent: what the dispatcher just added, without the
 * vehicle's own position marker and without pass-through points.
 */
const orderStops = (stops: RouteStop[], currentRoute?: Route2g): RouteStop[] =>
  stops.filter(
    (stop) =>
      !currentRoute?.stops.some((currentStop) => currentStop.id === stop.id) &&
      stop.action !== "passthrough" &&
      stop.action !== "from_driver_location",
  )

/**
 * Distance of the whole drive, from where the vehicle stands now to the last
 * stop. `remaining_distance` is cumulative from the vehicle's current
 * position, so the last stop already carries it. This is the same span the map
 * totals for the route.
 */
const wholeDriveDistance = (stops: RouteStop[]): number =>
  stops.at(-1)?.remaining_distance ?? 0

/**
 * Distance of the order alone: from the first stop the dispatcher just added
 * to the last one. The run to get there belongs to whatever the driver is
 * carrying now, not to this order.
 */
const orderOnlyDistance = (
  stops: RouteStop[],
  currentRoute?: Route2g,
): number => {
  const own = orderStops(stops, currentRoute)
  const start = own.at(0)?.remaining_distance
  const end = own.at(-1)?.remaining_distance
  if (start === undefined || end === undefined) return wholeDriveDistance(stops)
  return end - start
}

/**
 * The number quoted on the total-distance line.
 *
 * The line always carries the order-only span: from the first newly added stop
 * to the last one. When the message also carries the approach line it breaks
 * the drive down as "approach (empty run) + order (loaded km)", which is what
 * dispatchers and drivers read on the order. Without the approach line there
 * is nowhere to put the empty-run distance, so the order-only span is still
 * used — quoting the whole drive would silently bill the driver for finishing
 * his previous order (one reported a 776 km run sent to him as 1373 km).
 */
const messageDistance = (
  stops: RouteStop[],
  currentRoute: Route2g | undefined,
  _showApproachDistance: boolean,
): number => orderOnlyDistance(stops, currentRoute)

/**
 * Distance of the approach leg ("dolot"): from the start of the approach to
 * the first newly added stop. `remaining_distance` is cumulative from the
 * vehicle's current position, so the approach starts either at the last stop
 * of the driver's current route that precedes the first new stop, or — when
 * there is no such stop — at the vehicle's current position (baseline 0).
 *
 * @param stops - Stops of the full (merged) route, in driving order
 * @param currentRoute - The driver's current route (its stops are not "new")
 * @returns Approach distance in meters, or undefined when it can't be derived
 */
export const computeApproachDistance = (
  stops: RouteStop[],
  currentRoute?: Route2g,
): number | undefined => {
  if (!stops || stops.length === 0) return undefined

  const isExistingStop = (stop: RouteStop) =>
    Boolean(
      currentRoute?.stops.some((currentStop) => currentStop.id === stop.id),
    )

  const firstNewStop = orderStops(stops, currentRoute).at(0)
  if (!firstNewStop || firstNewStop.remaining_distance === undefined)
    return undefined

  const firstNewIndex = stops.indexOf(firstNewStop)
  let baseline = 0
  for (let i = firstNewIndex - 1; i >= 0; i--) {
    if (isExistingStop(stops[i])) {
      baseline = stops[i].remaining_distance ?? 0
      break
    }
  }

  const approachDistance = firstNewStop.remaining_distance - baseline
  return approachDistance >= 0 ? approachDistance : undefined
}

/**
 * Updates the distance portion of an existing message.
 * The distance is calculated from the provided stops (from first to last stop).
 *
 * Only the distance on the official total-distance line (built from
 * `templates.totalDistance`) is replaced. Any other "<number> km" the user may
 * have typed elsewhere in the message is left untouched.
 *
 * @param message - The current message text
 * @param stops - The route stops to calculate distance from
 * @param currentRoute - The driver's current route (to calculate distance only for new stops)
 * @param preferredLanguage - Language whose template anchors the distance line
 * @param showApproachDistance - Whether the message carries the approach line,
 *   which decides the span of the total (see `messageDistance`)
 * @returns The message with updated distance
 */
export const updateDistanceInMessage = (
  message: string,
  stops: RouteStop[],
  currentRoute?: Route2g,
  preferredLanguage = "pl",
  showApproachDistance = false,
): string => {
  if (!stops || stops.length === 0) return message

  // Filter only new stops (stops not in currentRoute)
  const newStops = currentRoute?.stops
    ? stops.filter(
        (newStop) =>
          !currentRoute.stops.some(
            (currentStop) => newStop.id === currentStop.id,
          ),
      )
    : stops

  // If no new stops, don't update the message
  if (!newStops || newStops.length === 0) return message

  // Use the same calculation as generateRouteMessage for consistency
  const newDistance = formatKm(
    messageDistance(stops, currentRoute, showApproachDistance),
  )

  // Anchor the replacement to the total-distance label so we only touch the
  // official distance line, never a "<number> km" the user typed elsewhere.
  const templates: LangTemplate =
    routeMessageTemplates[
      preferredLanguage as keyof typeof routeMessageTemplates
    ] || routeMessageTemplates.pl

  const [labelPrefix] = templates.totalDistance.split("{distance}")
  // The distance token sits right after the label on the same line, so keep
  // the inter-token whitespace to a single space/tab (no newlines).
  const distancePattern = new RegExp(
    `(${escapeRegExp(labelPrefix)})-?\\d+[ \\t]*km`,
  )

  let updated = message.replace(distancePattern, `$1${newDistance}`)

  // Refresh the approach-distance line too, but only when the message already
  // contains one (i.e. it was generated with the approach-distance feature on).
  const approachDistance = computeApproachDistance(stops, currentRoute)
  if (approachDistance !== undefined) {
    const [approachLabelPrefix] = templates.approachDistance.split("{distance}")
    const approachPattern = new RegExp(
      `(${escapeRegExp(approachLabelPrefix)})-?\\d+[ \\t]*km`,
    )
    updated = updated.replace(
      approachPattern,
      `$1${formatKm(approachDistance)}`,
    )
  }

  return updated
}

// "-22 °C" for a set point, "2 – 8 °C" for a range. A one-sided bound is
// spelt "min"/"max" rather than the Polish "od"/"do" of the dispatcher's UI,
// because this line goes out in the driver's language.
export const formatTemperatureForDriver = (
  min?: number,
  max?: number,
): string | undefined => {
  if (min == null && max == null) return undefined
  if (min != null && max != null) {
    return min === max ? `${min} °C` : `${min} – ${max} °C`
  }
  return min != null ? `min ${min} °C` : `max ${max} °C`
}

export const generateRouteMessage = ({
  route,
  currentRoute,
  driverName = "",
  preferredLanguage = "pl",
  routeData,
  vhost,
  hideCoordinates,
  hideDistanceLine = false,
  hideStopTimes = false,
  showFixedTimes = false,
  showApproachDistance = false,
  hideOrderNumber = posthog.isFeatureEnabled(HIDE_ORDER_NUMBER_FLAG) === true,
}: {
  route: Route2g
  currentRoute?: Route2g | undefined
  routeData?: OrderAnalyzeRoute | undefined
  driverName?: string | undefined
  preferredLanguage?: string | undefined
  vhost?: string | undefined
  hideCoordinates?: boolean
  hideDistanceLine?: boolean
  hideStopTimes?: boolean
  showFixedTimes?: boolean
  showApproachDistance?: boolean
  hideOrderNumber?: boolean
}) => {
  // define templates type
  const templates: LangTemplate =
    routeMessageTemplates[
      preferredLanguage as keyof typeof routeMessageTemplates
    ] || routeMessageTemplates.pl

  const newStops = route.stops
    .filter(
      (newStop) =>
        !currentRoute?.stops.some(
          (currentStop) => newStop.id === currentStop.id,
        ),
    )
    .filter((stop) => stop.action !== "passthrough")

  const distance = formatKm(
    messageDistance(route.stops, currentRoute, showApproachDistance),
  )

  // Approach leg ("dolot") from the current position or the last existing
  // stop to the first newly added stop.
  const approachDistance = showApproachDistance
    ? computeApproachDistance(route.stops, currentRoute)
    : undefined

  const textSummary = getTextSummary(
    newStops,
    routeData?.routeMetaData?.stops,
    templates,
    hideCoordinates,
    hideStopTimes,
    showFixedTimes,
  )

  // Build additional information from route-meta if available
  let additionalInfo = ""
  if (routeData?.routeMetaData) {
    const meta = routeData.routeMetaData
    const parts = []

    // Add weight if available
    if (meta.weight) {
      parts.push(
        templates.weight.replace(
          "{weight}",
          `${meta.weight ?? ""}`.replace(" kg", ""),
        ),
      )
    }

    // Pallet exchange and the required temperature, when the order states
    // them. The driver reads these before loading, so they sit with the weight.
    if (meta.pallet_exchange !== undefined) {
      parts.push(
        templates.palletExchange.replace(
          "{value}",
          meta.pallet_exchange ? templates.yes : templates.no,
        ),
      )
    }
    const temperature = formatTemperatureForDriver(
      meta.temperature_min,
      meta.temperature_max,
    )
    if (temperature) {
      parts.push(templates.temperature.replace("{temperature}", temperature))
    }

    // Add the order number if available. The `{refNumber}` placeholder name
    // is a contract with the released mobile app — only its source changed.
    const orderNumber = meta.order_number
    if (orderNumber && !hideOrderNumber) {
      parts.push(templates.refNumber.replace("{refNumber}", orderNumber))
    }

    // Add notes if available
    if (meta.notes) {
      parts.push(templates.notes.replace("{notes}", meta.notes))
    }

    if (parts.length > 0) {
      additionalInfo = `\n\n${parts.join("\n")}`
    }
  }

  if (shouldShowOrderMessageExtraNotes(vhost)) {
    additionalInfo = additionalInfo + "\n\n" + templates.pallets
  }

  const greeting = templates.greeting.replace("{driverName}", driverName)
  const approachInfo =
    approachDistance !== undefined
      ? `. ${templates.approachDistance.replace(
          "{distance}",
          formatKm(approachDistance),
        )}`
      : ""
  const orderIntro = hideDistanceLine
    ? templates.newOrderIntro
    : `${templates.newOrderIntro} ${templates.totalDistance.replace("{distance}", distance)}${approachInfo}`
  const confirmation = templates.confirmation

  return [
    greeting,
    orderIntro,
    `${textSummary}${additionalInfo}`,
    confirmation,
  ].join("\n\n")
}
